import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import queryString from 'query-string'
import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { PageFormatType, SingleTemplateSettings } from '@rio/types'
import PreviewPanel, { useDocumentDownload } from 'builder/components/PreviewPanel'
import {
  DocumentCustomizationModalSwitcher,
  prettifySize,
  useTemplateCustomization,
  useUpdateTemplateSettings,
} from 'builder/components/DocumentCustomizationModal'
import {
  actions,
  ColorChangeHandler,
  FontChangeHandler,
  LayoutFormatChangeHandler,
  PrefillStatuses,
  SizeChangeHandler,
  SpacingChangeHandler,
  TemplateChangeHandler,
  selectors,
  AFTER_USE_EXAMPLE_HIGHLIGHT_ID,
  SettingsFieldChangeHandler,
} from 'builder/modules/resumeEditor'
import {
  ResumeCritiqueStatuses,
  actions as critiqueActions,
} from 'builder/modules/resumeCritiqueReview'
import {
  selectors as resumeReviewSelectors,
  actions as resumeReviewActions,
} from 'builder/modules/resumeReview'
import { selectors as userSelectors, UserType } from 'builder/modules/user'
import { actions as coverLetterActions } from 'builder/modules/coverLetterEditor'
import { actions as panelActions } from 'builder/modules/panel'
import { actions as coRegActions } from 'builder/modules/coRegistration'
import { actions as generalEditorActions, EditorPanelType } from 'builder/modules/generalEditor'
import { navigate } from 'builder/modules/navigate'
import TunerPrePaywallModal from 'builder/components/TunerPrePaywallModal'
import { JobPosting } from 'builder/components/DocumentEditor/JobPosting/JobPosting'
import AiCoverletterTailoredView from 'builder/components/DocumentEditor/AICoverLetter/AiCoverletterTailoredView'
import SavingInfoBanner from 'builder/components/JobTracking/SavingInfoBanner/SavingInfoBanner'
import { AICoverLetter } from 'builder/components/DocumentEditor/JobPosting/AiCoverLetter'
import EditAIJobDetails from 'builder/components/DocumentEditor/AICoverLetter/EditAIJobDetails'
import { DocumentTypes, FetchStatuses, FORMATS, SPACING } from 'builder/modules/constants'
import { ErrorState } from 'builder/components/DocumentEditor/AICoverLetter/ErrorState'
import { ButtonSize } from 'builder/components/Button'
import AbandonmentModal from 'builder/components/AbandonmentModal'
import AbandonmentResumeModal from 'builder/components/AbandonmentResumeModal'
import { HAS_ABANDONMENT_RESUME_MODAL_BEEN_SHOW } from 'builder/components/AbandonmentResumeModal/constants'
import Paywall from 'builder/components/AiFeatures/Paywall'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useConfig } from 'builder/hooks/useConfig'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { MouseClickEvent, actions as uiActions } from 'builder/modules/ui'
import {
  ResumeDistributionModal,
  useResumeDistributionPromo,
} from 'builder/components/ResumeDistributionModal'
import { useStepByStepFeature } from 'builder/components/DocumentEditor/StepByStepFlow/hooks/useStepByStepFeature'
import { OverviewModal } from 'builder/components/AutoTailoring/OverviewModal'
import { useResumeReviewFeatureConfig } from 'builder/hooks/useResumeReviewFeatureConfig'
import {
  AiResumeReviewMobilePanel,
  AiResumeReviewPaywall,
} from 'builder/components/AiFeatures/AiResumeReview'
import { CritiqueFinishedBanner } from 'builder/components/CritiqueFinishedBanner/CritiqueFinishedBanner'
import { IFRAME_PARAMS } from 'builder/modules/resumeCritiqueReview/constants'
import {
  TailoringFromPublicJobBoardHeader,
  getAutotailoringHeaderHeight,
} from 'builder/components/DocumentEditor/TailoringFromPublicJobBoard'
import SelfTailorResumeModal from 'builder/components/SelfTailorResumeModal'
import { DocumentTour, TourModal } from 'builder/components/DocumentEditor/DocumentTour'
import { EditorPanelSwitcherItem } from 'builder/components/EditorPanelSwitcher'
import { useI18n } from 'builder/hooks/useI18n'
import { ResumeEditorWithTabs } from 'builder/components/DocumentEditor/ResumeEditorWithTabs'
import ModalAIReviewResult from 'builder/components/DocumentEditor/ModalAIReviewResult/ModalAIReviewResult'
import { ResumeEditorSwitcher } from './ResumeEditorSwitcher'
import { Container, LeftSide } from './styles'
import { useSetAccountDataIfEmpty } from './hooks/useSetAccountDataIfEmpty'
import { useAutoTailoringFromJobBoard } from './hooks/useAutoTailoringFromJobBoard'

export const ResumeEditorView = () => {
  const location = useLocation()
  const routeParams = useParams()
  const [searchParams] = useSearchParams()
  const mediaQueries = useMediaQueries()
  const config = useConfig()
  const dispatch = useDispatch()
  const { i18n } = useI18n()

  const isNewBuilderEnabled = config?.features.isNewBuilderEnabled
  const user = useTypedSelector(state => state.user)
  const isPremiumUser = useTypedSelector(userSelectors.premiumAccess)
  const isDownloading = useTypedSelector(state => state.rendering.isDocumentDownloading)
  const editorState = useTypedSelector(state => state.generalEditor)
  const resumeState = useTypedSelector(state => state.resumeEditor)
  const coverLetterState = useTypedSelector(state => state.coverLetterEditor)
  const panelState = useTypedSelector(state => state.panel)
  const isPlansPageAbandonmentModalOpen = useTypedSelector(
    state => state.ui.isPlansPageAbandonmentModalOpen,
  )
  const [hasAbandonmentModalBeenShown, setHasAbandonmentModalBeenShown] = useState(false)
  const [isUserLeaving, setIsUserLeaving] = useState(false)
  const isCustomizeModalOpen = useTypedSelector(
    state => state.resumeEditor.editorPanelType === 'customization',
  )
  const isAiReviewPanelOpen = useTypedSelector(
    state => state.resumeEditor.editorPanelType === 'ai_review',
  )

  const hasShowReview = searchParams.get('showReview') === 'true'
  const [showReviewModal, setShowReviewModal] = useState<boolean>(hasShowReview)
  const [showAIHighlight, setShowAIHighlight] = useState<boolean>(false)

  const resumeId = (routeParams.id && parseInt(routeParams.id)) || 0

  const [resumeDistributionModalDownloadFormat, setResumeDistributionModalDownloadFormat] =
    useState<FORMATS | null>(null)
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const { status: prefillingStatus } = useTypedSelector(selectors.prefill)
  const isPrefilling = prefillingStatus === PrefillStatuses.scheduled

  const critique = useTypedSelector(state => resumeReviewSelectors.critique(state, resumeId))
  const critiqueReviewStatus = critique?.status
  const isCritiqueReviewStatusFinished = critiqueReviewStatus === ResumeCritiqueStatuses.finished
  const critiqueEntryPoints = config?.features.critiqueEntryPoints

  const {
    resume,
    isSyncing,
    isOptimizerModalOpen,
    isOpenSavingInfoBanner,
    changeJobPostingLink,
    isClickedOnAiCVBanner,
    optimizerSection: {
      jobPostingAPIData: { recommendedJobTitle },
    },
    AICoverLetter: {
      generateAICoverLetterStatus,
      isOpenAICVOptimizerModal,
      isClickedOnAiCVEditModalIcon,
      isShowUntailoreView,
    },
    aiGenerationPaywallType,
    editorPanelType,
    aiResumeReview: { isAiResumeReviewPaywallModalOpen },
  } = resumeState

  const { updateTemplateSettings } = useUpdateTemplateSettings(resume)
  const { supportsCustomColor, selectedLayoutFormatOption, supportsNewCustomization } =
    useTemplateCustomization(resume)

  const { handleDownloadDocument } = useDocumentDownload({
    currentDocument: resume,
    downloadClickEventSource: isCustomizeModalOpen ? `full_screen_resume_preview` : `resume_editor`,
    downloadActionEventSource: isCustomizeModalOpen ? 'builder_full_screen_preview' : 'builder',
  })

  const { isAiResumeReviewFeatureActive } = useResumeReviewFeatureConfig()
  const { currentStep, isRbpStepFlowActive } = useStepByStepFeature()
  const isDataLoaded = !!resume && editorState.isLoaded

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const { shouldShowRDPromo, setHasSeenPromo } = useResumeDistributionPromo()

  const isMobileAiReviewPanelOpen = !!resume && isAiReviewPanelOpen && !mediaQueries.isAboveTablet
  const isSharingEnabled = (config?.features.shareResume ?? false) && isPremiumUser

  const previewSwitcherItem: EditorPanelSwitcherItem<EditorPanelType> = {
    text: i18n.t('builder.resume_editor.ai_review'),
    id: 'ai_review',
  }

  const mobilePanelSwitcherItems: EditorPanelSwitcherItem<EditorPanelType>[] = [
    { text: i18n.t('builder.resume_editor.edit'), id: 'editor' },
    {
      text: i18n.t('builder.resume_editor.preview'),
      id: 'customization',
      dataTestId: AFTER_USE_EXAMPLE_HIGHLIGHT_ID,
    },
    ...(isAiResumeReviewFeatureActive ? [previewSwitcherItem] : []),
  ]

  const abandonmentModalListener = useCallback((e: MouseEvent) => {
    if (e.pageY <= 5) {
      setIsUserLeaving(false)
    }
  }, [])

  const init = useCallback(() => {
    // fetch data and initialize related modules
    if (!resumeId) {
      return
    }

    dispatch(actions.fetchResumeRequest({ id: resumeId }))
    dispatch(generalEditorActions.fetchEditorPayloadRequest())
    dispatch(coRegActions.initialize())

    // show the preview modal on mobiles
    const params = queryString.parse(location.search, { parseNumbers: true })
    if ('preview' in params && !mediaQueries.isDesktop) {
      dispatch(actions.setIsCustomizeModalOpen(true))
    }
    // track the page opening

    const isDataLayerPushed = localStorage.getItem('DATA_LAYER_PUSH')
    const hasAbandonmentModalBeenShown = localStorage.getItem(
      HAS_ABANDONMENT_RESUME_MODAL_BEEN_SHOW,
    )

    if (hasAbandonmentModalBeenShown === 'true') {
      setHasAbandonmentModalBeenShown(true)
    }

    if (user?.data?.socialAccount && !(isDataLayerPushed === 'true')) {
      localStorage.setItem('DATA_LAYER_PUSH', 'true')
      trackMarketingEvent('Sign up', 'Complete Registration')
    }

    if (panelState.documentsStatus !== 'LOADED') {
      // If someone has opened a resume directly, we call this action to get info of user documents
      dispatch(panelActions.fetchDocuments())
    }

    document.addEventListener('mousemove', abandonmentModalListener)
  }, [
    abandonmentModalListener,
    dispatch,
    location.search,
    mediaQueries.isDesktop,
    panelState.documentsStatus,
    resumeId,
    user?.data?.socialAccount,
  ])

  useEffect(() => {
    init()

    return () => {
      dispatch(actions.setIsCustomizeModalOpen(false))
    }
  }, [routeParams.id])

  useAutoTailoringFromJobBoard(resumeId)

  useSetAccountDataIfEmpty({
    firstNameFromResume: resume?.firstName,
    lastNameFromResume: resume?.lastName,
  })

  useEffectOnMount(() => {
    return () => {
      document.removeEventListener('mousemove', abandonmentModalListener)
    }
  })

  useEffect(() => {
    dispatch(resumeReviewActions.fetchCritiques())
  }, [dispatch])

  useEffect(() => {
    const {
      isClickedOnAiCVBanner,
      optimizerSection: {
        jobPostingAPIData: { recommendedJobTitle },
      },
      AICoverLetter: { isShowUntailoreView },
    } = resumeState

    const isJobPostingLinked = recommendedJobTitle !== null && recommendedJobTitle !== ''

    const AICoverLetterTailoredView =
      isClickedOnAiCVBanner && isJobPostingLinked && !isShowUntailoreView

    if (AICoverLetterTailoredView) {
      trackInternalEvent('see_job_posting_selection_screen')
    }

    if (resumeState.error) navigate('/resumes')
  }, [resumeState])

  const sendResumeToCritiqueReview = (resumeId: number) => {
    if (user?.data?.billingInfo.userType === UserType.free) {
      return
    }

    trackInternalEvent('click_send_resume_review', { where: 'dash' })

    dispatch(
      resumeReviewActions.sendResumeToCritique({
        resumeId,
      }),
    )
  }

  useEffect(() => {
    const resumeIdToSendToCritique = sessionStorage.getItem('sendToCritique')
    if (resumeIdToSendToCritique) {
      sendResumeToCritiqueReview(Number(resumeIdToSendToCritique))
      sessionStorage.removeItem('sendToCritique')
    }
  }, [])

  const handleFullSizeOpen = useCallback(
    (e?: MouseClickEvent) => {
      e && e.preventDefault()
      dispatch(actions.setIsCustomizeModalOpen(true))
    },
    [dispatch],
  )

  const handleFullSizeClose = useCallback(() => {
    dispatch(actions.setIsCustomizeModalOpen(false))
  }, [dispatch])

  const handleBannerClose = useCallback(() => {
    dispatch(actions.setOpenSavingInfoBanner(false))
  }, [dispatch])

  const handleBannerOpen = useCallback(() => {
    navigate('/job-tracking')
    trackInternalEvent('open_job_tracking')
  }, [])

  const handleAbandonmentModalClose = useCallback(() => {
    setHasAbandonmentModalBeenShown(true)
    setIsUserLeaving(false)
  }, [])

  const handleTemplateUpdate: TemplateChangeHandler = useCallback(
    id => {
      dispatch(actions.updateTemplate({ id }))
      trackInternalEvent('change_resume_template', { template: id })
    },
    [dispatch],
  )

  const resetGenerateAICoverLetterStatus = useCallback(() => {
    dispatch(actions.setGenerateAICoverLetterStatus(FetchStatuses.notAsked))
  }, [dispatch])

  const onRetryAIGenerateErrorStateModal = useCallback(() => {
    resetGenerateAICoverLetterStatus()
    dispatch(actions.setIsClickedOnAiCVBanner(true))
  }, [dispatch, resetGenerateAICoverLetterStatus])

  const {
    aiGenerationSection: { showPaywall: showAICoverLetterPaywall },
  } = coverLetterState

  const showResumeOptimizerModal = isOptimizerModalOpen && resume && user.data
  const isJobPostingLinked = recommendedJobTitle !== null && recommendedJobTitle !== ''
  const showAICoverLetterTailoredView =
    isClickedOnAiCVBanner && isJobPostingLinked && !isShowUntailoreView
  const howAiCoverLetterNormalView =
    (isClickedOnAiCVBanner && !isJobPostingLinked) ||
    isOpenAICVOptimizerModal ||
    isShowUntailoreView

  const showGenerateCLErrorStateModal = generateAICoverLetterStatus === FetchStatuses.failed

  const errorStateButtonSize = !mediaQueries.isPhone ? ButtonSize.default : ButtonSize.small
  const inBuilderAbandonmentFlag = config?.features.inBuilderAbandonment || false
  const showAbandonmentModal =
    !hasAbandonmentModalBeenShown &&
    [UserType.free, UserType.postPremium].includes(user?.data?.billingInfo.userType as UserType) &&
    isUserLeaving &&
    inBuilderAbandonmentFlag &&
    user?.data?.showAbandonmentFlows

  const autotailoringHeaderHeight = autoTailoringFromPublicJobBoard.enabled
    ? getAutotailoringHeaderHeight(mediaQueries)
    : 0

  useLayoutEffect(() => {
    if (!isRbpStepFlowActive && resume?.id && resume?.id === resumeId) {
      trackInternalEvent('visit_resume_editor', { resume_id: resume?.id, editor: 'builder' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRbpStepFlowActive, resume?.id])

  const handleClosePaywall = useCallback(() => {
    if (showAICoverLetterPaywall) {
      dispatch(coverLetterActions.setShowAICoverLetterPaywall())
    } else {
      dispatch(actions.setAiGenerationPaywallType(null))
    }
  }, [dispatch, showAICoverLetterPaywall])

  const handleColorSelect: ColorChangeHandler = useCallback(
    color => {
      if (color && supportsCustomColor && supportsNewCustomization) {
        updateTemplateSettings({
          settingsName: 'color',
          value: color,
        })
      } else {
        dispatch(
          actions.updateSimpleField({
            name: 'color',
            value: color,
            debounce: false,
          }),
        )
      }
      trackInternalEvent('change_resume_color', { template: resume?.template })
    },
    [
      supportsCustomColor,
      supportsNewCustomization,
      updateTemplateSettings,
      dispatch,
      resume?.template,
    ],
  )

  const handleChangeLayoutFormat: LayoutFormatChangeHandler = useCallback(
    value => {
      if (typeof value !== 'string') return
      updateTemplateSettings({
        settingsName: 'format',
        value: value as PageFormatType,
      })
      trackInternalEvent('change_page_size_format', {
        old_value: selectedLayoutFormatOption,
        new_value: value,
        template: resume?.template,
      })
    },
    [resume?.template, updateTemplateSettings, selectedLayoutFormatOption],
  )

  const handleFontChange: FontChangeHandler = useCallback(
    ({ value, newFontType, selectedPrimaryFont, selectedSecondaryFont, selectedTertiaryFont }) => {
      if (!resume?.template || typeof value !== 'string' || !newFontType) return

      const oldValue =
        newFontType === 'primaryFont'
          ? selectedPrimaryFont
          : newFontType === 'secondaryFont'
          ? selectedSecondaryFont
          : selectedTertiaryFont

      const eventFontType =
        newFontType === 'primaryFont'
          ? 'primary'
          : newFontType === 'secondaryFont'
          ? 'secondary'
          : 'tertiary'

      updateTemplateSettings({
        settingsName: newFontType,
        value,
      })

      trackInternalEvent('change_resume_font', {
        label: eventFontType,
        old_value: oldValue,
        new_value: value,
        template: resume?.template,
      })
    },
    [resume?.template, updateTemplateSettings],
  )

  const handleSizeChange: SizeChangeHandler = useCallback(
    (sizeNew: number, sizeOld: number) => {
      if (!resume?.template || typeof sizeNew !== 'number') return
      updateTemplateSettings({
        settingsName: 'fontSize',
        value: sizeNew,
      })
      trackInternalEvent('change_resume_font_size', {
        old_value: prettifySize(sizeOld),
        new_value: prettifySize(sizeNew),
        template: resume.template,
      })
    },
    [resume?.template, updateTemplateSettings],
  )

  const handleSpacingChange: SpacingChangeHandler = useCallback(
    (spacingNew: number, spacingOld: number) => {
      const normalize = (value: number) => Math.min(Math.max(value, SPACING.min), SPACING.max)

      if (supportsNewCustomization) {
        if (typeof spacingNew !== 'number') return
        updateTemplateSettings({
          settingsName: 'lineSpacing',
          value: spacingNew,
        })
      } else {
        dispatch(
          actions.updateSimpleField({
            name: 'spacing',
            value: normalize(spacingNew),
            debounce: true,
          }),
        )
      }

      trackInternalEvent('change_line_spacing', {
        label: 'templates',
        direction: spacingNew > spacingOld ? 'increase' : 'decrease',
        old_value: spacingOld,
        new_value: spacingNew,
      })
    },
    [dispatch, supportsNewCustomization, updateTemplateSettings],
  )

  const handleSettingsFieldChange: SettingsFieldChangeHandler = useCallback(
    (settingsName, value, oldValue) => {
      updateTemplateSettings({
        settingsName,
        value,
      })

      const events: Partial<Record<keyof SingleTemplateSettings, string>> = {
        marginsBetweenCards: 'resume_between_content_blocks_paddings',
        verticalPageMargins: 'resume_top_bottom_margins',
        horizontalPageMargins: 'resume_left_right_margins',
        marginsBetweenSections: 'resume_between_sections_paddings',
        marginsBetweenSectionNameAndContent: 'resume_titles_content_paddings',
        fontSizePrimaryHeading: 'font_size_primary_heading',
        fontSizeSecondaryHeading: 'font_size_secondary_heading',
        fontSizeBody: 'font_size_body',
        fontSizeSectionTitles: 'font_size_section_titles',
        primaryFontWeight: 'font_weight_primary',
        secondaryFontWeight: 'font_weight_secondary',
        bodyFontWeight: 'font_weight_body',
        sectionTitlesFontWeight: 'font_weight_section_titles',
        headerAlignment: 'header_alignment',
        dateAlignment: 'date_alignment',
        locationAlignment: 'location_alignment',
        dateFormat: 'date_format',
        skillsLayout: 'resume_skills_layout',
        educationLayout: 'resume_education_layout',
        educationOrder: 'resume_education_order',
      }

      if (events[settingsName]) {
        trackInternalEvent('customise_resume_template', {
          template: resume?.template,
          label: events[settingsName],
          old_value: oldValue,
          new_value: value,
        })
      }
    },
    [updateTemplateSettings, resume?.template],
  )

  const handleEditorPanelTypeChange = useCallback(
    (type: EditorPanelType) => {
      dispatch(actions.setEditorPanelType(type))
      trackInternalEvent('switch_resume_editor_tab', {
        tab: type,
      })
    },
    [dispatch],
  )

  const viewResumeCritiqueReview = (url?: string) => {
    if (url) {
      trackInternalEvent('click_read_resume_critique_banner', { where: 'builder' })
      dispatch(critiqueActions.setCritiquePageVisitedCount(true))
      window.open(`${url}${IFRAME_PARAMS}`)
    }
  }

  const showResumeDistributionModal = useMemo(() => {
    if (shouldShowRDPromo) {
      return (format: FORMATS) => setResumeDistributionModalDownloadFormat(format)
    }
  }, [shouldShowRDPromo])

  const handleShareDocument = useMemo(() => {
    if (isSharingEnabled) {
      return () => {
        trackInternalEvent('click_share_button', {
          source: isCustomizeModalOpen ? 'full_screen_resume_preview' : 'resume_editor',
        })
        dispatch(uiActions.openSharingModal(resumeId))
      }
    }
  }, [dispatch, isSharingEnabled, isCustomizeModalOpen, resumeId])

  const triggerDownloadFromResumeDistributionModal = useCallback(() => {
    if (!resumeDistributionModalDownloadFormat) {
      return
    }

    handleDownloadDocument({
      format: resumeDistributionModalDownloadFormat,
    })
  }, [resumeDistributionModalDownloadFormat, handleDownloadDocument])

  const handleDownloadButtonMenuOpen = useCallback(() => {
    trackInternalEvent('click_download_resume_formats', {
      source: isCustomizeModalOpen ? 'full_screen_resume_preview' : 'resume_editor',
      template: resume?.template,
    })
  }, [resume?.template, isCustomizeModalOpen])

  const triggerAIReviewHightlight = () => setShowAIHighlight(true)

  return (
    <>
      {isDataLoaded && <TourModal />}
      {isDataLoaded && <DocumentTour />}
      {autoTailoringFromPublicJobBoard.enabled && !isCustomizeModalOpen && !isNewBuilderEnabled && (
        <TailoringFromPublicJobBoardHeader />
      )}
      <TunerPrePaywallModal />
      {(autoTailoringFromPublicJobBoard.enabled &&
        autoTailoringFromPublicJobBoard.processingAutoTailoring !== FetchStatuses.notAsked) ||
      (!autoTailoringFromPublicJobBoard.enabled && resume?.autoTailored && resume.draft) ? (
        <OverviewModal />
      ) : isNewBuilderEnabled ? (
        <ResumeEditorWithTabs
          editorState={editorState}
          resumeState={resumeState}
          showResumeDistributionModal={showResumeDistributionModal}
          handleDownloadDocument={handleDownloadDocument}
          handleShareDocument={handleShareDocument}
        />
      ) : (
        <Container data-testid="resume-editor-view" $heightCompensation={autotailoringHeaderHeight}>
          {isCritiqueReviewStatusFinished && critiqueEntryPoints && (
            <CritiqueFinishedBanner
              reviewerName={critique?.reviewer.name || 'Joe Miller'}
              viewResumeCritiqueReview={() => viewResumeCritiqueReview(critique?.iframeUrl || '')}
            />
          )}

          <LeftSide>
            <ResumeEditorSwitcher
              editorState={editorState}
              resumeState={resumeState}
              isDataLoaded={isDataLoaded}
              user={user}
              handleOpenCustomize={handleFullSizeOpen}
              mobilePanelSwitcherItems={mobilePanelSwitcherItems}
              showResumeDistributionModal={showResumeDistributionModal}
              handleDownloadDocument={handleDownloadDocument}
              handleShareDocument={handleShareDocument}
            />
          </LeftSide>

          {/**
           * Unmount PreviewPanel when DocumentPreviewModal is visible. Reasons:
           * - prevent render if block is invisible - reduce the load of user's device
           * - prevent parallel tasks - pdfjs worker can't do more than one in parallel
           */}
          {!isCustomizeModalOpen && mediaQueries.isDesktop && resume && (
            <PreviewPanel
              top={autotailoringHeaderHeight}
              type={DocumentTypes.resume}
              onFullSize={handleFullSizeOpen}
              documentId={resumeId}
              isSyncing={isSyncing}
              isDownloading={isDownloading}
              currentDocument={resume}
              isFullScreen={isCustomizeModalOpen}
              showResumeDistributionModal={showResumeDistributionModal}
              isOnline={isOnline}
              isPrefilling={isPrefilling}
              isAiResumeReviewFeatureActive={isAiResumeReviewFeatureActive}
              isEditorPanelTypeSwitcherEnabled={isAiResumeReviewFeatureActive && !currentStep}
              editorPanelType={editorPanelType}
              handleEditorPanelTypeChange={handleEditorPanelTypeChange}
              handleDownloadDocument={handleDownloadDocument}
              handleShareDocument={handleShareDocument}
              handleDownloadButtonMenuOpen={handleDownloadButtonMenuOpen}
              showAIHighlight={showAIHighlight}
              handleTurnOffAIHighlight={setShowAIHighlight}
            />
          )}

          {isCustomizeModalOpen && resume && (
            <DocumentCustomizationModalSwitcher
              type={DocumentTypes.resume}
              isSyncing={isSyncing}
              isDownloading={isDownloading}
              currentDocument={resume}
              editorState={editorState}
              onClose={handleFullSizeClose}
              handleDownloadDocument={handleDownloadDocument}
              updateTemplate={handleTemplateUpdate}
              showResumeDistributionModal={showResumeDistributionModal}
              registeredAt={user?.data?.registeredAt}
              showAbandonmentFlows={user?.data?.showAbandonmentFlows}
              handleColorSelect={handleColorSelect}
              handleChangeLayoutFormat={handleChangeLayoutFormat}
              handleFontChange={handleFontChange}
              handleSizeChange={handleSizeChange}
              handleSpacingChange={handleSpacingChange}
              editorPanelType={editorPanelType}
              handleEditorPanelTypeChange={handleEditorPanelTypeChange}
              handleShareDocument={handleShareDocument}
              handleDownloadButtonMenuOpen={handleDownloadButtonMenuOpen}
              mobilePanelSwitcherItems={mobilePanelSwitcherItems}
              handleSettingsFieldChange={handleSettingsFieldChange}
            />
          )}

          {isMobileAiReviewPanelOpen && (
            <AiResumeReviewMobilePanel
              editorPanelType={editorPanelType}
              handleEditorPanelTypeChange={handleEditorPanelTypeChange}
              mobilePanelSwitcherItems={mobilePanelSwitcherItems}
            />
          )}
        </Container>
      )}
      {showAICoverLetterTailoredView && <AiCoverletterTailoredView />}
      {showResumeOptimizerModal && <JobPosting resumeId={resumeId} />}
      {howAiCoverLetterNormalView && <AICoverLetter resumeId={resumeId} />}
      {isClickedOnAiCVEditModalIcon && <EditAIJobDetails />}
      {isOpenSavingInfoBanner && !mediaQueries.isPhone && !changeJobPostingLink && (
        <SavingInfoBanner onClose={handleBannerClose} onOpen={handleBannerOpen} />
      )}
      {aiGenerationPaywallType && (
        <Paywall onClose={handleClosePaywall} aiGenerationType={aiGenerationPaywallType} />
      )}
      {isPlansPageAbandonmentModalOpen && <AbandonmentModal />}
      {isAiResumeReviewPaywallModalOpen && <AiResumeReviewPaywall />}

      {showGenerateCLErrorStateModal && (
        <ErrorState
          onClose={resetGenerateAICoverLetterStatus}
          onTryAgain={onRetryAIGenerateErrorStateModal}
          ButtonSize={errorStateButtonSize}
        />
      )}
      {showAbandonmentModal && user.data && (
        <AbandonmentResumeModal
          handleClose={handleAbandonmentModalClose}
          hasUserVisitedPlans={user.data.visitedPlans}
          email={user.data.email}
          fullName={user.data.fullName}
        />
      )}

      {resumeDistributionModalDownloadFormat && (
        <ResumeDistributionModal
          onClose={() => setResumeDistributionModalDownloadFormat(null)}
          triggerDownload={triggerDownloadFromResumeDistributionModal}
          setHasSeenPromo={setHasSeenPromo}
        />
      )}
      <SelfTailorResumeModal />
      {showReviewModal && resumeState.resume && (
        <ModalAIReviewResult
          showModal={setShowReviewModal}
          triggerAIReviewHightlight={triggerAIReviewHightlight}
        />
      )}
    </>
  )
}
